import { Component, Injectable, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { skip, Subscription as RxSubscription } from 'rxjs';
import { CsvExportService } from 'src/app/services/csv-export.service';
import {
  PdfExportService,
  PdfTableConfig,
} from 'src/app/services/pdf-export.service';
import { IEvent } from 'src/modules/event/interfaces/event';
import { IProduct } from 'src/modules/product/interfaces/product';
import { IOrder } from '../../interfaces/order';
import { IOrderListConfig } from '../../interfaces/order-list-config';
import { OrderDataSource } from '../../services/order-data-source';
import { OrderNotesEdit } from '../order-notes/component';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  template: '',
  standalone: false,
})
@Injectable()
export abstract class OrderList implements OnInit, OnDestroy {
  public title: string;
  public displayedColumns: string[];
  public abstract Item: IEvent | IProduct;
  public DataSource: OrderDataSource;
  public Data: any;
  public totalTickets: number;
  public hasAuthorisedPurchase: boolean;
  public loading: boolean;
  public PageEvent: PageEvent;
  public csvExportLoading = false;
  public pdfExportLoading = false;

  public constructor(
    protected Auth: AuthService,
  ) { }

  protected abstract Dialog: MatDialog;
  protected abstract config: IOrderListConfig;
  protected Subscriptions: RxSubscription[] = [];

  protected abstract colunms: {
    displayName: any;
    name: string;
    width: string;
  }[];
  private latestPageRequest = 0;

  async ngOnInit() {
    await this.DataSource.connect();

    this.Subscriptions.push(
      this.DataSource.loading.subscribe((loading) => (this.loading = loading)),
    );
    this.Subscriptions.push(
      this.DataSource.PageEvent.subscribe(
        (pageEvent) => (this.PageEvent = pageEvent),
      ),
    );
    this.Subscriptions.push(
      this.DataSource.filter.pipe(skip(1)).subscribe(() =>
        this.loadPage(0),
      ),
    );
    this.Subscriptions.push(
      this.DataSource.totalTickets.subscribe(
        (totalTickets) => (this.totalTickets = totalTickets),
      ),
    );
    await this.loadPage(0);
  }

  public setSearch(search: string) {
    this.DataSource.setFilter('email', search);
  }

  createAddress = (selectedAddress) => {
    let addressString = '';
    const { address1, address2, city, county, country, postCode } =
      selectedAddress;
    addressString = address1;

    Object.values({ address2, city, county, country, postCode }).forEach(
      (line) => {
        if (line) {
          addressString = addressString + `, ${line}`;
        }
      },
    );

    return addressString;
  };

  public async downloadCSV() {
    if (this.csvExportLoading) {
      return;
    }

    this.csvExportLoading = true;
    try {
      this.exportCSV(
        (await this.DataSource.fetchAllGuestListPages(this.Item.id)).filter(
          this.isAuthorised,
        ),
      );
    } catch (error) {
      await this.DataSource.handleError(error);
    } finally {
      this.csvExportLoading = false;
    }
  }

  protected exportCSV(tableData: IOrder[]) {
    const ticketHeaders = [];
    const isSuperAdmin = this.Auth.inGroup('superadmin');

    tableData.map((item) => {
      Object.keys(item.order_details.item.options).map((option) => {
        if (!ticketHeaders.includes(option)) {
          ticketHeaders.push(option);
        }
      });
    });

    const data = tableData.map((item) => {
      let deliveryAddress = '';
      let billingAddress = '';

      if (item.order_details.shippingAddress) {
        deliveryAddress = this.createAddress(
          item.order_details.shippingAddress,
        );
      }
      if (item.order_details.billingAddress) {
        billingAddress = this.createAddress(item.order_details.billingAddress);
      }
      const ticketVariants = {};

      ticketHeaders.map((header) => {
        if (item.order_details.item.options[header]) {
          ticketVariants[header] = item.order_details.item.options[header].qty;
        } else {
          ticketVariants[header] = 0;
        }
      });

      const row = {
        id: item.order_id,
        uuid: item.order_details.uuid,
        order_id: item.order_id,
        user_id: item.user_id,
        name: item.first_name + ' ' + item.last_name,
        email: item.email,
        employee_number: item.employee_number,
        branch_number: item.branch_number,
        phone: item.phone,
        title: item.order_details.item.title,
        total: item.order_details.total,
        purchase_date: item.purchase_date,
        billing_address: billingAddress,
        delivery_address: deliveryAddress,
        notes: item.notes,
        status: item.status,
        ...ticketVariants,
        total_tickets: item.total_tickets,
        icon: item.icon,
        cohort: item.cohort ?? '',
      };
      if (!isSuperAdmin) {
        delete row.phone;
        delete row.billing_address;
        delete row.delivery_address;
      }
      return row;
    });
    const [headerRow] = data;
    const headers = Object.keys(headerRow).map((header) =>
      header === 'cohort' ? 'Cohort' : header,
    );
    const filename = this.getFileName();
    CsvExportService.export(data, filename, { headers, showLabels: true });
  }

  protected getColumns() {
    return this.colunms;
  }

  public async downloadPDF(): Promise<void> {
    if (this.pdfExportLoading) {
      return;
    }

    this.pdfExportLoading = true;
    try {
      this.exportPDF(
        (await this.DataSource.fetchAllGuestListPages(this.Item.id)).filter(
          this.isAuthorised,
        ),
      );
    } catch (error) {
      await this.DataSource.handleError(error);
    } finally {
      this.pdfExportLoading = false;
    }
  }

  protected exportPDF(orderList: IOrder[]): void {
    const filename = this.getFileName();
    const columns = this.getColumns();
    const headers = columns.map((col) => col.displayName);
    const widths = columns.map((col) => col.width);
    const rows = orderList.map((order: IOrder) =>
      columns.map((column) => order[column.name]),
    );

    const tableConfig: PdfTableConfig = { headers, rows, widths };
    PdfExportService.downloadTable(filename, tableConfig, filename);
  }

  public editOrderNotes(Order: IOrder) {
    const config = new MatDialogConfig();
    config.disableClose = true;
    config.data = { Order };
    this.Subscriptions.push(
      this.Dialog.open(OrderNotesEdit, config)
        .afterClosed()
        .subscribe(async (order) => {
          this.loading = true;
          !!order && (await this.DataSource.notes(order.order_id, order.notes));
          await this.loadPage(this.PageEvent?.pageIndex || 0);
        }),
    );
  }

  public refundOrder(Order: IOrder) {
    const config = new MatDialogConfig();
    config.disableClose = true;
    config.data = {
      Order,
      refund: true,
    };
    this.Dialog.open(OrderNotesEdit, config)
      .afterClosed()
      .subscribe(async (order) => {
        this.loading = true;
        !!order && (await this.DataSource.refund(order.order_id, order.notes));
        await this.loadPage(this.PageEvent?.pageIndex || 0);
      });
  }

  public ngOnDestroy() {
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }

  protected abstract getFileName(): string;

  protected isAuthorised({ status }: IOrder): boolean {
    return status === 'authorised';
  }

public async loadPage(pageEvent: PageEvent | number): Promise<void> {
  const pageIndex =
    typeof pageEvent === 'number' ? pageEvent : pageEvent.pageIndex;
  const request = ++this.latestPageRequest;

  try {
    const data = await this.DataSource.load(this.Item.id, pageIndex);

    if (request === this.latestPageRequest) {
      this.Data = data;
      this.hasAuthorisedPurchase = !!data.find(this.isAuthorised);
    }
  } catch (error) {
    if (request === this.latestPageRequest) {
      await this.DataSource.handleError(error);
    }
  }
}
}
